import { ArrowRight, Brain, Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useSearchParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Textarea,
} from "@/components/primitives";
import { extractQuestionText } from "../utils/qaUtils";

interface QAItem {
	question: string;
	answer: string;
}

interface QAStepProps {
	/**
	 * Current question being displayed
	 */
	currentQuestion: string;
	/**
	 * Current answer text from user input
	 */
	currentAnswer: string;
	/**
	 * History of answered questions
	 */
	qaHistory: QAItem[];
	/**
	 * Whether a question is being loaded from AI
	 */
	isLoadingQuestion: boolean;
	/**
	 * Error message, if any
	 */
	aiError: string | null;
	/**
	 * Decision summary from AI (after first answer)
	 */
	decisionSummary?: {
		title: string;
		description: string;
	} | null;
	/**
	 * Current question number (for tracking in tests)
	 */
	currentQuestionNumber?: number;

	/**
	 * Callback for when answer input changes
	 */
	onAnswerChange: (value: string) => void;
	/**
	 * Callback fired when user submits an answer
	 * Should handle: adding to history, getting next question, or transitioning to next step
	 */
	onSubmitAnswer: () => void;
	/**
	 * Optional subtitle for the QA section
	 */
	subtitle?: string;
}

const markdownPlugins = [remarkGfm];

export function QAStep({
	currentQuestion,
	currentAnswer,
	qaHistory,
	isLoadingQuestion,
	aiError,
	decisionSummary,
	currentQuestionNumber,
	onAnswerChange,
	onSubmitAnswer,
	subtitle,
}: QAStepProps) {
	const [searchParams] = useSearchParams();
	const conversationRef = useRef<HTMLDivElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	// Auto-scroll to bottom when conversation updates
	useEffect(() => {
		if (conversationRef.current) {
			const id = setTimeout(() => {
				conversationRef.current?.scrollTo({
					top: conversationRef.current.scrollHeight,
					behavior: "smooth",
				});
			}, 0);
			return () => clearTimeout(id);
		}
	}, [qaHistory, currentQuestion, isLoadingQuestion, aiError]);

	// Auto-focus textarea on mount and when question loads
	useEffect(() => {
		if (currentQuestion && !isLoadingQuestion && textareaRef.current) {
			textareaRef.current.focus();
		}
	}, [currentQuestion, isLoadingQuestion]);

	return (
		<Card className="w-full max-w-4xl mx-auto" data-testid="qa-step-container">
			{/* Suggested Reflection Context */}
			{searchParams.get("suggested") === "true" && (
				<div className="mb-6 px-6 pt-6">
					<div className="border rounded-lg p-4 bg-muted/30 dark:bg-muted/20 border-border text-left">
						<div className="mb-4">
							<h1 className="font-medium text-base mb-2 text-foreground">
								{decodeURIComponent(
									searchParams.get("title") || "Suggested Reflection",
								)}
							</h1>
							{searchParams.get("description") && (
								<p className="text-sm text-muted-foreground mb-3 leading-relaxed">
									{decodeURIComponent(searchParams.get("description") || "")}
								</p>
							)}
						</div>
						{searchParams.get("reasoning") && (
							<div className="mb-4">
								<h5 className="text-sm font-medium text-foreground mb-2">
									Why this reflection?
								</h5>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{decodeURIComponent(searchParams.get("reasoning") || "")}
								</p>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Header */}
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
						<Brain className="h-5 w-5 text-white" />
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-semibold">
							Questions
						</CardTitle>
						{subtitle && (
							<p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
						)}
					</div>
				</div>
			</CardHeader>

			{/* Content */}
			<CardContent className="pt-6">
				<div className="space-y-6">
					{/* Error Alert */}
					{aiError && (
						<div
							className="border border-red-200 dark:border-red-800 rounded-lg p-3 bg-red-50 dark:bg-red-900/20"
							data-testid="qa-question-error"
						>
							<p className="text-sm text-red-800 dark:text-red-200">
								{aiError}
							</p>
						</div>
					)}

					{/* Conversation History + Current Question */}
					{qaHistory.length > 0 ||
					currentQuestion ||
					isLoadingQuestion ||
					aiError ? (
						<div
							ref={conversationRef}
							className="bg-white dark:bg-gray-950 border rounded-lg p-4 min-h-[200px] max-h-[400px] overflow-y-auto"
							data-testid="qa-conversation-history"
						>
							<div className="space-y-4">
								{/* Past Q&A pairs */}
								{qaHistory.map((qa, index) => (
									<div
										key={index}
										className="space-y-2"
										data-testid={`qa-pair-${index + 1}`}
									>
										{/* Question */}
										<div className="flex gap-2">
											<div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0">
												<Brain className="h-4 w-4 text-gray-600 dark:text-gray-400" />
											</div>
											<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 flex-1 border border-gray-200 dark:border-gray-800">
												<div className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed space-y-2">
													<ReactMarkdown remarkPlugins={markdownPlugins}>
														{extractQuestionText(qa.question)}
													</ReactMarkdown>
												</div>
											</div>
										</div>

										{/* Answer */}
										<div className="flex gap-2 justify-end">
											<div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 max-w-[80%] border border-blue-200 dark:border-blue-800">
												<p className="text-sm text-blue-900 dark:text-blue-100">
													{qa.answer}
												</p>
											</div>
											<div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium text-blue-600 dark:text-blue-400">
												You
											</div>
										</div>
									</div>
								))}

								{/* Current Question or Loading/Error State */}
								{currentQuestion ? (
									<div className="flex gap-2" data-testid="qa-current-question">
										<div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0">
											<Brain className="h-4 w-4 text-gray-600 dark:text-gray-400" />
										</div>
										<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 flex-1 border border-gray-200 dark:border-gray-800">
											<div className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed space-y-2">
												<ReactMarkdown remarkPlugins={markdownPlugins}>
													{extractQuestionText(currentQuestion)}
												</ReactMarkdown>
											</div>
										</div>
									</div>
								) : isLoadingQuestion ? (
									<div
										className="flex gap-2"
										data-testid={`qa-question-loading-${currentQuestionNumber || 0}`}
									>
										<div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center flex-shrink-0">
											<Brain className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-pulse" />
										</div>
										<div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 flex-1 border border-blue-200 dark:border-blue-800">
											<p className="text-sm text-blue-800 dark:text-blue-200 italic">
												Thinking...
											</p>
										</div>
									</div>
								) : aiError ? (
									<div className="flex gap-2">
										<div className="w-8 h-8 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center flex-shrink-0">
											<Brain className="h-4 w-4 text-red-600 dark:text-red-400" />
										</div>
										<div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-3 flex-1 border border-red-200 dark:border-red-800">
											<p className="text-sm text-red-800 dark:text-red-200">
												{aiError.includes("suggested_reflection")
													? 'AI-suggested reflections require the "suggested_reflection" prompt to be configured in AI Prompts Management. Please contact your administrator.'
													: "AI conversation service is not properly configured. Please contact support to resolve this issue."}
											</p>
										</div>
									</div>
								) : null}
							</div>
						</div>
					) : null}

					{/* Answer Input Section */}
					<div className="space-y-3">
						<Textarea
							ref={textareaRef}
							value={currentAnswer}
							onChange={(e) => onAnswerChange(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									if (
										currentAnswer.trim() &&
										!isLoadingQuestion &&
										!(aiError && !currentQuestion)
									) {
										onSubmitAnswer();
									}
								}
								// Shift+Enter allows new line (default behavior, no need to handle)
							}}
							placeholder={
								aiError && !currentQuestion
									? "AI conversation service is not properly configured - please contact support"
									: "Type your response here... (Enter to send, Shift+Enter for new line)"
							}
							className="min-h-[100px]"
							disabled={isLoadingQuestion || (!!aiError && !currentQuestion)}
							data-testid="qa-answer-textarea"
						/>

						<div className="flex justify-end">
							<Button
								onClick={onSubmitAnswer}
								disabled={
									!currentAnswer.trim() ||
									isLoadingQuestion ||
									(!!aiError && !currentQuestion)
								}
								data-testid="qa-submit-button"
							>
								{isLoadingQuestion ? (
									<>
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										Processing...
									</>
								) : (
									<>
										Send
										<ArrowRight className="h-4 w-4 ml-2" />
									</>
								)}
							</Button>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
